import { FSA } from "flux-standard-action/lib";

import { Quantity, Unit, Measures } from "../../../../../rest/Misc";
import { SensorDraft, Sensor } from "../../../../../models/Sensor";
import { ServiceAccount } from "../../../../../models/ServiceAccount";
import { Vehicle } from "../../../../../models/Vehicle";
import { IdName, GeotabSensor } from "../../../../../models/IdName";
import { prepareSensorDraft } from "../../../../utils/draft";
import { assertArray, assertTrue, assertNumber, assertError, assertValue } from "../../../../utils/assert";

import { EditStore } from "../../EditCreate/EditStore";

import { names, MeasurePair } from './EditSensorActions';
import { names as editCreateNames } from "../../../../actions/EditCreateEntityActions";
import { Transform } from "../../../../../flux/Store";
import { EntityType } from "../../../../../models/EntityType";

let session: number = 0;

export interface EditSensorStoreState
{
    draft: SensorDraft;

    vehicles: Vehicle[];
    geotabSensors: GeotabSensor[];
    
    quantities: Quantity[];
    units: Unit[];

    editSession: number;
}
type StateKeys = keyof EditSensorStoreState;

export class EditSensorStore extends EditStore<SensorDraft, EditSensorStoreState>
{
    constructor()
    {
        super ();
        this.register<number>(names.SET_VEHICLE, e => this.draft({ VehicleId: e.payload }));
        this.register<GeotabSensor>(names.SET_GEOTAB, e => {
            const idName = e.payload || { Id: null, Name: null, QuantityId: null };
            return this.draft({ GeotabId: idName.Id, GeotabTitle: idName.Name });
        });
        this.register<number>(names.SET_ABS_RED_MAX, e => this.draft({ AbsLevelMaximumRed: e.payload }));
        this.register<number>(names.SET_ABS_RED_MIN, e => this.draft({ AbsLevelMinimumRed: e.payload }));
        this.register<number>(names.SET_ABS_YELLOW_MAX, e => this.draft({ AbsLevelMaximumYellow: e.payload }));
        this.register<number>(names.SET_ABS_YELLOW_MIN, e => this.draft({ AbsLevelMinimumYellow: e.payload }));
        this.register<number>(names.SET_REL_RED_MAX, e => this.draft({ RelLevelMaximumRed: e.payload }));
        this.register<number>(names.SET_REL_RED_MIN, e => this.draft({ RelLevelMinimumRed: e.payload }));
        this.register<number>(names.SET_REL_YELLOW_MAX, e => this.draft({ RelLevelMaximumYellow: e.payload }));
        this.register<number>(names.SET_REL_YELLOW_MIN, e => this.draft({ RelLevelMinimumYellow: e.payload }));
        this.register<string>(names.SET_DISPLAY, e => this.draft({ Display: e.payload }));
        this.register<MeasurePair>(names.SET_QUANTITY_UNIT, e => this.draft({ QuantityId: e.payload.quantityId, UnitId: e.payload.unitId }))
        this.register<number>(names.SET_BASELINE, e => this.setBaseline(e.payload));
        
        this.register<Vehicle[]>(names.SET_VEHICLES_ITEMS, e => this.field({ vehicles: e.payload }));
        this.register<GeotabSensor[]>(names.SET_GEOTAB_SENSORS_ITEMS, e => this.field({ geotabSensors: e.payload }));
        this.register<Measures>(names.SET_MEASURE_ITEMS, e => this.field({
                quantities: e.payload.Quantities,
                units: e.payload.Units
            })
        );

        this.register<Readonly<Sensor>>(editCreateNames.SET_ENTITY_PROTOTYPE, e => {
            if(e.meta === EntityType.Sensor) {
                assertValue(e.payload, 'sensor payload has to have a value');
                const sensor = Object.assign({}, e.payload, { id: null, ServiceAccountId: null, ServiceAccount: null, GeotabId: null, GeotabTitle: null, Display: null, CanOnlyEditLevels: null});
                return this.getDefaultStateFor(sensor);
            }
        });
        this.register<Readonly<Sensor>>(editCreateNames.SET_EDIT_ENTITY, e => {
            if(e.meta === EntityType.Sensor)
                return this.getDefaultStateFor(e.payload);
        });
        this.register(editCreateNames.REJECT_DRAFT, e => {
            if(e.meta === EntityType.Sensor)
                return this.getDefaultStateFor(null);
        });
    }

    setBaseline = (baseline: number): EditSensorStoreState => {
        assertTrue(baseline === null || typeof baseline === 'number', 'number payload expected');
        return this.draft({ Baseline: baseline });
    };

    private getDefaultStateFor(sensor: Readonly<Sensor>): EditSensorStoreState
    {
        return {
            draft: prepareSensorDraft(sensor),
            vehicles: [],
            geotabSensors: [],
            quantities: [],
            units: [],
            editSession: session++
        };
    }
}


// WEBPACK FOOTER //
// ./src/app/components/Pages/Sensor/flux/EditSensorStore.ts